import { AmountType, WorkspaceType } from "@shared/types";
import { toFixed } from "@shared/utils/numbers";
import AppTheme from "@shared/common/Themes/AppTheme";
import { UUID_REGEX } from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import { selectWorkspace } from "@shared/slices/common.slice";
import { USER_WORKSPACE_ENDPOINTS } from "@shared/features/workspaces/constants";
import { getIsPercentAmountType } from "@shared/features/bots/utils";

export const getDefaultAmountPerTradeValue = (
  availableAmount: number,
  minAmount: number,
  selectedAmountType: AmountType,
  defaultPercent: number | undefined,
  amountPrecision: number,
  useDefaultPercent?: boolean,
  ratioFromAvailableAmount = 0.1
) => {
  let defaultAmount;
  if (getIsPercentAmountType(selectedAmountType)) {
    if (useDefaultPercent) return defaultPercent;
    defaultAmount = defaultPercent || 0;
  } else {
    const defaultsAmounts = (
      selectedAmountType === AmountType.FIXED_USD_AMOUNT
        ? DEFAULT_USD_AMOUNTS
        : DEFAULT_BTC_AMOUNTS
    ).sort((a, b) => b - a);

    defaultAmount = defaultsAmounts[defaultsAmounts.length - 1];
    for (let amount of defaultsAmounts) {
      if (amount <= availableAmount * ratioFromAvailableAmount) {
        defaultAmount = amount;
        break;
      }
    }
  }
  return toFixed(
    Math.min(availableAmount, Math.max(minAmount, defaultAmount)),
    amountPrecision
  );
};

const DEFAULT_USD_AMOUNTS = [
  1, 2.5, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000,
];
const DEFAULT_BTC_AMOUNTS = [
  0.1, 0.05, 0.02, 0.01, 0.005, 0.002, 0.001, 0.0005, 0.0001,
];

export const showListQueryEmptyState = (
  graphData: any[] | undefined,
  isLoading: boolean | undefined,
  isSuccess: boolean | undefined
) => {
  return !isLoading && isSuccess && graphData && graphData.length <= 1;
};

export const capitalizeFirstLetter = (string: string | undefined) => {
  if (!string) return;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getPriceTextColor = (
  prevPrice: number,
  currentPrice: number,
  theme: typeof AppTheme
) => {
  if (prevPrice) {
    if (currentPrice < prevPrice) return theme.colors.red;
    else if (currentPrice > prevPrice) return theme.colors.green;
  }
  return theme.colors.primaryText;
};

export const isValidUuid = (uuid?: string): uuid is string =>
  !!uuid && UUID_REGEX.test(uuid) && uuid.length === 36;

export const getStore = () => sharedLib.getState().store.getState() as any;

export const getStoreWorkspace = () => selectWorkspace(getStore());

export const isDemoQuery = (url: string) => {
  const workspace = getStoreWorkspace();
  return workspace === WorkspaceType.DEMO && !USER_WORKSPACE_ENDPOINTS.has(url);
};

export const getIsDemoQueryArgs = (
  args: void | { is_demo_query?: boolean },
  url?: string
) => {
  return (
    args?.is_demo_query ||
    (getStoreWorkspace() === WorkspaceType.DEMO &&
      args?.is_demo_query !== false &&
      (!url || !USER_WORKSPACE_ENDPOINTS.has(url)))
  );
};
